import React, { useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * SegmentedControl — a second-level switcher drawn as chips in a track.
 *
 * Use it when a view already has a primary tab bar. Repeating the underline
 * treatment one row below it produces two identical bars stacked on each other
 * and nothing tells the reader which level they are on; a filled track reads as
 * "a control inside this panel" instead of "navigation".
 *
 * Implements the WAI-ARIA tabs pattern with automatic activation: a roving
 * tabindex (only the selected chip is in the tab order) plus Arrow/Home/End,
 * which the hand-rolled `role="tab"` bars around the app do not provide.
 *
 *   <SegmentedControl
 *     ariaLabel="Shipment information"
 *     value={section}
 *     onChange={setSection}
 *     options={[{ value: 'plans', label: 'Shipment Plans', count: 3 }]}
 *   />
 */
const SIZES = {
  sm: { track: 'tw-p-0.5', chip: 'tw-h-7 tw-px-2.5 tw-text-xs', gap: 'tw-gap-1.5' },
  md: { track: 'tw-p-1', chip: 'tw-h-8 tw-px-3 tw-text-sm', gap: 'tw-gap-2' },
};

const SegmentedControl = ({ options, value, onChange, ariaLabel, size = 'md', idPrefix, className = '' }) => {
  const buttonsRef = useRef([]);
  const sizing = SIZES[size] || SIZES.md;

  const selectAt = index => {
    const next = options[(index + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    buttonsRef.current[(index + options.length) % options.length]?.focus();
  };

  const onKeyDown = (event, index) => {
    const keys = {
      ArrowRight: () => selectAt(index + 1),
      ArrowLeft: () => selectAt(index - 1),
      Home: () => selectAt(0),
      End: () => selectAt(options.length - 1),
    };
    const handler = keys[event.key];
    if (!handler) return;
    event.preventDefault();
    handler();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`tw-inline-flex tw-items-center ${sizing.gap} ${sizing.track} tw-rounded-lg tw-bg-slate-100 ${className}`}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            ref={element => { buttonsRef.current[index] = element; }}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${option.value}` : undefined}
            aria-controls={idPrefix ? `${idPrefix}-panel-${option.value}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={event => onKeyDown(event, index)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 ${sizing.chip} tw-rounded-md tw-border-0 tw-font-semibold tw-whitespace-nowrap tw-cursor-pointer tw-touch-manipulation tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 ${
              selected
                ? 'tw-bg-white tw-text-slate-900 tw-shadow-sm'
                : 'tw-bg-transparent tw-text-slate-500 hover:tw-text-slate-800'
            }`}
          >
            {Icon && <Icon size={12} aria-hidden="true" className={selected ? 'tw-text-primary-600' : 'tw-text-slate-400'} />}
            {option.label}
            {option.count > 0 && (
              <span className={`tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-text-[10px] tw-font-bold tw-leading-none tw-tabular-nums ${
                selected ? 'tw-bg-primary-50 tw-text-primary-700' : 'tw-bg-slate-200 tw-text-slate-600'
              }`}>
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

SegmentedControl.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number,
    icon: PropTypes.elementType,
  })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  /** Set to link each chip to its panel via aria-controls / aria-labelledby. */
  idPrefix: PropTypes.string,
  className: PropTypes.string,
};

export default SegmentedControl;
